import { useLazyQuery, useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { GetBPApplicationStatus } from 'api/graphQL/queries/projectBulk.queries'
import { BackendProcessStatus } from 'api/graphQL/types/backendProcess.types'
import { processEnv } from 'components/configuration/environmentVariables'
import React from 'react'

/**
 * Custom hook to track Best Practice application progress.
 * Handles polling backend status and showing progress dialog.
 *
 * @param bestPracticeAutomationInstanceId - The instance ID returned from addProductFamilyToProject mutation
 * @param onComplete - Optional callback to execute when BP application completes successfully
 * @returns Object containing BP progress state and dialog props
 */
export const useBPTracking = (
    bestPracticeAutomationInstanceId?: string,
    onComplete?: () => void
) => {
    const [bpProgress, setBpProgress] = React.useState<number>(0)
    const [bpInProgress, setBpInProgress] = React.useState(false)
    const [bpInstance, setBpInstance] = React.useState<string>()
    const isMountedRef = React.useRef(true)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    // Cleanup on unmount
    React.useEffect(() => {
        return () => {
            isMountedRef.current = false
            setBpInProgress(false)
        }
    }, [])

    // BP status checking query
    const [getStatus, { data: statusData, error: statusError }] = useLazyQuery<{
        getBestPracticeApplicationStatus: Partial<BackendProcessStatus>
    }>(GetBPApplicationStatus, {
        variables: {
            input: {
                instanceId: bpInstance,
            },
        },
        onCompleted: (data) => {
            if (!isMountedRef.current) return
            setBpProgress(
                data?.getBestPracticeApplicationStatus?.progressPercentage ?? 0
            )
        },
        onError: (error) => {
            if (!isMountedRef.current) return
            setBpInProgress(false)
            setBpProgress(0)
            showGlobalSnackbar({
                variables: {
                    message: `Failed to check Best Practice status: ${error.message}`,
                    variant: 'error',
                    id: 'BPApplicationStatusError',
                },
            })
        },
        fetchPolicy: 'no-cache',
    })

    // Start polling when instanceId is provided
    React.useEffect(() => {
        if (!bestPracticeAutomationInstanceId) return
        if (!(processEnv.REACT_APP_EnableSmartProjectCopy === '1')) return

        setBpInstance(bestPracticeAutomationInstanceId)
    }, [bestPracticeAutomationInstanceId])

    // Start polling when instanceId is set
    React.useEffect(() => {
        if (!isMountedRef.current) return

        // Start polling when we have an instance and haven't started yet
        if (bpInstance && !bpInProgress) {
            setBpInProgress(true)
            getStatus()
        }
    }, [bpInstance, bpInProgress, getStatus])

    // Polling loop
    React.useEffect(() => {
        if (!(processEnv.REACT_APP_EnableSmartProjectCopy === '1')) return
        if (!statusData && !statusError) return
        if (!isMountedRef.current) {
            setBpInProgress(false)
            return
        }

        const state = statusData?.getBestPracticeApplicationStatus?.state

        // Check if process is complete
        if (state === 'Complete') {
            setBpInProgress(false)
            setBpProgress(0)
            showGlobalSnackbar({
                variables: {
                    message: 'Best Practices have successfully been applied!',
                    variant: 'success',
                    id: 'BestPracticesAppliedSuccess',
                },
            })
            // Clear instance to allow new BP processes
            setBpInstance(undefined)

            // Execute completion callback if provided
            if (onComplete) {
                onComplete()
            }
            return
        }

        // Check if process errored
        if (statusError || state === 'Error') {
            setBpInProgress(false)
            setBpProgress(0)
            showGlobalSnackbar({
                variables: {
                    message:
                        'Best Practice application failed. Please try again.',
                    variant: 'error',
                    id: 'BestPracticesApplicationError',
                },
            })
            setBpInstance(undefined)
            return
        }

        // Only continue polling if still in progress
        if (
            bpInProgress &&
            isMountedRef.current &&
            state !== 'Complete' &&
            state !== 'Error'
        ) {
            // Poll again after 3 seconds
            const queryTimeout = setTimeout(() => {
                if (isMountedRef.current && bpInProgress) {
                    getStatus()
                }
            }, 3000)
            return () => {
                clearTimeout(queryTimeout)
            }
        }
    }, [statusData, statusError, bpInProgress, showGlobalSnackbar, getStatus])

    return {
        bpProgress,
        bpInProgress,
        // Dialog props to spread onto ProgressDialog
        progressDialogProps: bpInProgress
            ? {
                  dialogId: 'familyAddBPProgressBar',
                  dialogName: 'Applying Best Practices',
                  open: true,
                  statusText:
                      bpProgress > 0
                          ? `Applying Best Practices (${Math.round(
                                bpProgress
                            )} % complete)...`
                          : 'Preparing to apply Best Practices...',
                  progressPercentage: bpProgress,
                  variant: 'Add Family' as const,
                  showProgressPercentage: bpProgress > 0,
              }
            : null,
    }
}
